import React from 'react';
import { reduxStore } from 'services/gecko-store/redux';
import CONSTS from 'constants/index.js';
import { GeckoToastService, GeckoModalService } from 'ui';
import { renderError } from 'services/gecko-error';
import GeckoRequest from 'services/gecko-request';
import {
  fetchImport,
  fetchAnswers,
  fetchImportDeltas,
} from 'routes/bots/actions';
import BotAnswersImportUploadModal from '../UploadModal';
import GeckoLocation from 'services/gecko-location';
import GeckoSocket from 'services/gecko-socket';

const fetchBotTags = (botId) => {
  return GeckoRequest.get(`/bots/${botId}/tags`).then((tags) => {
    return reduxStore.dispatch({ type: 'REPLACE_BOT_TAGS', payload: tags });
  });
};

export const ImportReviewService = {
  openUploadModal: (botId) => {
    return new GeckoModalService()
      .open(
        () => {
          return <BotAnswersImportUploadModal botId={botId} />;
        },
        { backdrop: 'static' }
      )
      .then((importId) => {
        if (!importId) {
          return;
        }
        // Add importId to bot
        ImportReviewService.dispatch.updateBotImportId(botId, importId);
        // Fetch import
        fetchImport({ botId, importId });
        // Navigate to review page
        GeckoLocation.goTo(`/bots/${botId}/answers-import-review/${importId}`);
        // Update import after it has been processed
        ImportReviewService.socket.processed(botId, importId);
      })
      .catch(renderError);
  },

  approve: (botId, importId, delta) => {
    return ImportReviewService.http
      .approveDelta(botId, delta.id)
      .then(() => {
        // Update delta review_status
        ImportReviewService.dispatch.approveDelta(delta);
        // Update import summary count
        ImportReviewService.dispatch.updateImportCount(importId, delta);
      })
      .catch(renderError);
  },

  reject: (botId, importId, delta) => {
    return ImportReviewService.http
      .rejectDelta(botId, delta.id)
      .then(() => {
        // Update delta review_status
        ImportReviewService.dispatch.rejectDelta(delta);
        // Update import summary count
        ImportReviewService.dispatch.updateImportCount(importId, delta);
      })
      .catch(renderError);
  },

  approveAll: (botId, importId) => {
    return ImportReviewService.http
      .approveAllDeltas(botId, importId)
      .then(() => {
        // Update delta review_status
        ImportReviewService.dispatch.approveAllDeltas();
        // Update redux store
        ImportReviewService.dispatch.updateImportCountApproveAll(importId);
      })
      .catch(renderError);
  },

  rejectAll: (botId, importId) => {
    return ImportReviewService.http
      .rejectAllDeltas(botId, importId)
      .then(() => {
        // Update delta review_status
        ImportReviewService.dispatch.rejectAllDeltas();
        // Update redux store
        ImportReviewService.dispatch.updateImportCountRejectAll(importId);
      })
      .catch(renderError);
  },

  confirm: (botId, importId) => {
    return ImportReviewService.http
      .confirmImport(botId, importId)
      .then(() => {
        fetchImport({ botId, importId });
        // Update bot after import is completed
        ImportReviewService.socket.completed(botId);
      })
      .catch(renderError);
  },

  cancel: (botId, importId) => {
    return ImportReviewService.http
      .cancelImport(botId, importId)
      .then(() => {
        // Remove bot_import_id from bot ref redux store
        ImportReviewService.dispatch.updateBotImportId(botId, null);
      })
      .catch(renderError);
  },

  socket: {
    processed: (botId, importId) => {
      const fn = (data) => {
        if (data.bot_id === Number(botId)) {
          // Fetch import
          fetchImport({ botId, importId });
          // Fetch import deltas
          fetchImportDeltas({ botId, importId });
          // Remove listener ref
          GeckoSocket.removeListener(`botimport:processed`, fn);
        }
      };
      GeckoSocket.on(`botimport:processed`, fn);
    },
    completed: (botId) => {
      const fn = (data) => {
        if (data.bot_id === Number(botId)) {
          // Show success toast
          new GeckoToastService().success('Bot import completed.');
          // Get the new tags
          fetchBotTags(botId);
          // Update the answers
          fetchAnswers({ botId });
          // Remove listener ref
          GeckoSocket.removeListener(`botimport:completed`, fn);
        }
      };
      GeckoSocket.on(`botimport:completed`, fn);
    },
  },

  http: {
    approveDelta: (botId, deltaId) =>
      GeckoRequest.post(`/bots/${botId}/imports/approve-delta/${deltaId}`),
    rejectDelta: (botId, deltaId) =>
      GeckoRequest.post(`/bots/${botId}/imports/reject-delta/${deltaId}`),
    approveAllDeltas: (botId, importId) =>
      GeckoRequest.post(`/bots/${botId}/imports/${importId}/approve-all`),
    rejectAllDeltas: (botId, importId) =>
      GeckoRequest.post(`/bots/${botId}/imports/${importId}/reject-all`),
    confirmImport: (botId, importId) =>
      GeckoRequest.post(`/bots/${botId}/imports/${importId}/commit`),
    cancelImport: (botId, importId) =>
      GeckoRequest.post(`/bots/${botId}/imports/${importId}/cancel`),
  },

  dispatch: {
    updateBotImportId: (id, bot_import_id) => {
      return reduxStore.dispatch({
        type: 'UPDATED_BOT_IMPORT_ID',
        payload: { id, bot_import_id },
      });
    },
    updateImportCount: (id, delta) => {
      return reduxStore.dispatch({
        type: 'UPDATED_IMPORT_COUNT',
        payload: { id, delta },
      });
    },
    updateImportCountApproveAll: (id) => {
      return reduxStore.dispatch({
        type: 'UPDATED_IMPORT_COUNT_APPROVE_ALL',
        payload: { id },
      });
    },
    updateImportCountRejectAll: (id) => {
      return reduxStore.dispatch({
        type: 'UPDATED_IMPORT_COUNT_REJECT_ALL',
        payload: { id },
      });
    },
    approveDelta: (data) => {
      const delta = Object.assign({}, data, {
        review_status: CONSTS.DELTA_REVIEW_STATUS_APPROVED,
      });
      return reduxStore.dispatch({
        type: 'UPDATED_DELTA',
        payload: { delta },
      });
    },
    rejectDelta: (data) => {
      const delta = Object.assign({}, data, {
        review_status: CONSTS.DELTA_REVIEW_STATUS_REJECTED,
      });
      return reduxStore.dispatch({
        type: 'UPDATED_DELTA',
        payload: { delta },
      });
    },
    approveAllDeltas: () => reduxStore.dispatch({ type: 'APPROVE_ALL_DELTAS' }),
    rejectAllDeltas: () => reduxStore.dispatch({ type: 'REJECT_ALL_DELTAS' }),
  },
};

export default ImportReviewService;
